import { useState, useEffect } from "react"
import { useMutation } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Sparkles, X, MapPin, Sun, Sunset, Moon, Lightbulb } from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { GuideResult, GuideRequest } from "@fohet/caravan-booker-shared/schemas"

interface Props {
    arealId: string
    arealName: string
}

type Group = GuideRequest["group"]
type Transport = GuideRequest["transport"]
type Interest = GuideRequest["interest"]
type WizardStep = 1 | 2 | 3
type PanelState = "closed" | "wizard" | "loading" | "result" | "error"

const STORAGE_KEY = "ai-guide-panel-open"

export const AIGuidePanel = ({ arealId, arealName }: Props) => {
    const { t, i18n } = useTranslation()

    const [panelState, setPanelState] = useState<PanelState>("closed")
    const [wizardStep, setWizardStep] = useState<WizardStep>(1)
    const [group, setGroup] = useState<Group | null>(null)
    const [transport, setTransport] = useState<Transport | null>(null)
    const [result, setResult] = useState<GuideResult | null>(null)

    // Restore open state from localStorage
    useEffect(() => {
        if (localStorage.getItem(STORAGE_KEY) === "true") {
            setPanelState("wizard")
        }
    }, [])

    const open = () => {
        localStorage.setItem(STORAGE_KEY, "true")
        setWizardStep(1)
        setGroup(null)
        setTransport(null)
        setResult(null)
        setPanelState("wizard")
    }

    const close = () => {
        localStorage.removeItem(STORAGE_KEY)
        setPanelState("closed")
    }

    const guideMutation = useMutation({
        mutationFn: (interest: Interest) =>
            api.post<GuideResult>(`/areals/${arealId}/guide`, {
                group,
                transport,
                interest,
                lang: i18n.language,
            }),
        onSuccess: (data) => {
            setResult(data)
            setPanelState("result")
        },
        onError: () => {
            setPanelState("error")
        },
    })

    const selectGroup = (g: Group) => {
        setGroup(g)
        setWizardStep(2)
    }

    const selectTransport = (tr: Transport) => {
        setTransport(tr)
        setWizardStep(3)
    }

    const selectInterest = (interest: Interest) => {
        setPanelState("loading")
        guideMutation.mutate(interest)
    }

    const groups: { value: Group; label: string; emoji: string }[] = [
        { value: "couple", label: t("aiGuide.group.couple"), emoji: "💑" },
        { value: "family", label: t("aiGuide.group.family"), emoji: "👨‍👩‍👧" },
        { value: "solo", label: t("aiGuide.group.solo"), emoji: "🧍" },
        { value: "friends", label: t("aiGuide.group.friends"), emoji: "👯" },
    ]

    const transports: { value: Transport; label: string; emoji: string }[] = [
        { value: "walk", label: t("aiGuide.transport.walk"), emoji: "🚶" },
        { value: "bike", label: t("aiGuide.transport.bike"), emoji: "🚲" },
        { value: "car", label: t("aiGuide.transport.car"), emoji: "🚗" },
        { value: "train", label: t("aiGuide.transport.train"), emoji: "🚆" },
    ]

    const interests: { value: Interest; label: string; emoji: string }[] = [
        { value: "history", label: t("aiGuide.interest.history"), emoji: "🏛️" },
        { value: "nature", label: t("aiGuide.interest.nature"), emoji: "🌲" },
        { value: "industrial", label: t("aiGuide.interest.industrial"), emoji: "🏭" },
        { value: "mix", label: t("aiGuide.interest.mix"), emoji: "✨" },
    ]

    return (
        <>
            {/* Floating button — always visible when panel is closed */}
            {panelState === "closed" && (
                <button
                    onClick={open}
                    className="fixed top-20 right-4 z-40 flex items-center gap-2 bg-brand text-white font-black text-sm rounded-[28px] px-4 py-2.5 shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200"
                >
                    <Sparkles className="size-4" />
                    <span className="hidden sm:inline">{t("aiGuide.buttonLabel")}</span>
                </button>
            )}

            {/* Panel — slides in from right */}
            {panelState !== "closed" && (
                <>
                    {/* Mobile backdrop */}
                    <div
                        className="fixed inset-0 bg-black/30 z-40 lg:hidden"
                        onClick={close}
                    />

                    {/* Panel */}
                    <div className="fixed top-0 right-0 h-full w-full sm:w-96 z-50 flex flex-col bg-white shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <Sparkles className="size-5 text-brand" />
                                <span className="font-black text-slate-900">{t("aiGuide.title")}</span>
                            </div>
                            <button
                                onClick={close}
                                className="size-8 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                            >
                                <X className="size-4" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            {/* Camp name */}
                            <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-slate-400 mb-5">
                                <MapPin className="size-3.5 shrink-0" />
                                <span className="truncate">{arealName}</span>
                            </div>

                            {/* Step 1 — group */}
                            {panelState === "wizard" && wizardStep === 1 && (
                                <WizardStepView
                                    title={t("aiGuide.step1Title")}
                                    options={groups}
                                    onSelect={(v) => selectGroup(v as Group)}
                                />
                            )}

                            {/* Step 2 — transport */}
                            {panelState === "wizard" && wizardStep === 2 && (
                                <WizardStepView
                                    title={t("aiGuide.step2Title")}
                                    options={transports}
                                    onSelect={(v) => selectTransport(v as Transport)}
                                    onBack={() => setWizardStep(1)}
                                />
                            )}

                            {/* Step 3 — interest */}
                            {panelState === "wizard" && wizardStep === 3 && (
                                <WizardStepView
                                    title={t("aiGuide.step3Title")}
                                    options={interests}
                                    onSelect={(v) => selectInterest(v as Interest)}
                                    onBack={() => setWizardStep(2)}
                                />
                            )}

                            {/* Loading */}
                            {panelState === "loading" && (
                                <div className="flex flex-col items-center justify-center py-16 gap-4">
                                    <div className="size-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                                    <p className="text-sm font-bold text-slate-500 text-center">{t("aiGuide.loading")}</p>
                                </div>
                            )}

                            {/* Error */}
                            {panelState === "error" && (
                                <div className="flex flex-col items-center gap-4 py-12">
                                    <p className="text-sm font-bold text-red-500 text-center">{t("aiGuide.error")}</p>
                                    <button
                                        onClick={open}
                                        className="text-sm font-black text-brand hover:underline"
                                    >
                                        {t("common.back")}
                                    </button>
                                </div>
                            )}

                            {/* Result */}
                            {panelState === "result" && result && (
                                <GuideResultView guide={result} />
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

// ─── Wizard step ────────────────────────────────────────────────────────────

interface WizardStepViewProps {
    title: string
    options: { value: string; label: string; emoji: string }[]
    onSelect: (value: string) => void
    onBack?: () => void
}

const WizardStepView = ({ title, options, onSelect, onBack }: WizardStepViewProps) => {
    const { t } = useTranslation()
    return (
        <div>
            <h3 className="text-base font-black text-slate-900 mb-4">{title}</h3>
            <div className="grid grid-cols-2 gap-2">
                {options.map((opt) => (
                    <button
                        key={opt.value}
                        onClick={() => onSelect(opt.value)}
                        className="flex flex-col items-center gap-2 p-4 bg-slate-50 border border-slate-200 rounded-2xl hover:border-brand hover:bg-brand/5 active:scale-95 transition-all duration-150 text-center"
                    >
                        <span className="text-2xl">{opt.emoji}</span>
                        <span className="text-sm font-black text-slate-700">{opt.label}</span>
                    </button>
                ))}
            </div>
            {onBack && (
                <button
                    onClick={onBack}
                    className="mt-4 text-xs font-black text-slate-400 hover:text-slate-600 transition-colors"
                >
                    ← {t("common.back")}
                </button>
            )}
        </div>
    )
}

// ─── Result view ─────────────────────────────────────────────────────────────

interface GuideResultViewProps {
    guide: GuideResult
}

const GuideResultView = ({ guide }: GuideResultViewProps) => {
    const { t } = useTranslation()

    const slots = [
        { key: "morning", icon: <Sun className="size-4 text-amber-400" />, label: t("aiGuide.morning"), data: guide.guide.morning },
        { key: "afternoon", icon: <Sunset className="size-4 text-orange-400" />, label: t("aiGuide.afternoon"), data: guide.guide.afternoon },
        { key: "evening", icon: <Moon className="size-4 text-indigo-400" />, label: t("aiGuide.evening"), data: guide.guide.evening },
    ] as const

    return (
        <div>
            <h3 className="text-base font-black text-slate-900 mb-4 leading-snug">{guide.guide.title}</h3>

            <div className="space-y-3">
                {slots.map(({ key, icon, label, data }) => (
                    <div key={key} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                            {icon}
                            <span className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</span>
                            <span className="ml-auto text-xs font-bold text-slate-400">{data.time}</span>
                        </div>
                        <p className="text-sm font-black text-slate-800 mb-1">{data.place}</p>
                        <p className="text-sm font-bold text-slate-500 leading-relaxed">{data.desc}</p>
                    </div>
                ))}
            </div>

            {/* Local tip */}
            <div className="mt-4 bg-brand/10 border border-brand/30 rounded-2xl p-4 flex gap-3">
                <Lightbulb className="size-4 text-brand shrink-0 mt-0.5" />
                <div>
                    <p className="text-xs font-black uppercase tracking-widest text-brand mb-1">{t("aiGuide.tip")}</p>
                    <p className="text-sm font-bold text-slate-700 leading-relaxed">{guide.guide.mskTip}</p>
                </div>
            </div>

            {guide.fromCache && (
                <p className="mt-3 text-center text-xs font-bold text-slate-400">{t("aiGuide.fromCache")}</p>
            )}
        </div>
    )
}
